import { LitElement, html } from 'lit';
import { styles } from './css';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { logErrorToSentry } from '../../helpers/helpers';
import { imagebossUrl } from '../../helpers/imageboss';
import { pushDataLayerEvent } from '../../helpers/data-layer';
import '../../lib/lit-3form-block/components/cta-button';
import '../form-controls/tf-text-field';
import '../form-controls/tf-textarea';
import {
  getTokenFromMetaTag as getCSRFToken,
  HEADER_PARAM as CSRF_HEADER_PARAM,
} from '@Helpers/csrf';

import { Core as Uppy, AwsS3 } from 'uppy';

import { fireAdobeAnalyticsEvent } from '../../helpers/adobe-analytics';
import { createFormAnalytics } from '../../helpers/form-analytics';

export class QuoteRequests extends LitElement {
  static get styles() {
    return [styles];
  }

  static properties = {
    _submitSuccess: { type: Boolean },
    _submitButtonDisabled: { type: Boolean },
    _formFields: { type: Array },
    _fileList: { type: Array },
    _showValidationPrompt: { type: Boolean },
    _referenceImageUrl: { type: String },
    _referenceImageDescription: { type: String },
    usedLocalStorage: { type: Boolean },
    completedFirstUpdated: { type: Boolean },
  };

  constructor() {
    super();

    this._formFields = this._formFieldsData;
    this._fileList = [];
    this._submitSuccess = false;
    this._submitButtonDisabled = false;
    this._showValidationPrompt = false;
    this._referenceImageUrl = '';
    this._referenceImageDescription = '';
    this._visualization = null;
    // Graphic Pattern Designer leads carry no toolName.
    this._visualizationTool = 'Graphic Pattern Designer';
    this.usedLocalStorage = false;
    this.completedFirstUpdated = false;
    this.formAnalytics = createFormAnalytics('Contact|Request a Quote', () =>
      this.shadowRoot?.querySelector('#quote-request-wrapper')
    );

    this.addEventListener('files-added', e => {
      this._fileList.push(e.detail.data);
      this.requestUpdate();
    });
  }

  firstUpdated() {
    this.configureUppy();
    this.populateFields();
    this.formAnalytics.view();

    setTimeout(() => {
      if (this.usedLocalStorage) {
        this.addReferenceImageToUppy();
      }
      this.completedFirstUpdated = true;
    }, 50);
  }

  render() {
    return html`
      <div id="quote-request-wrapper" class="page-wrapper">
        <div class="header">
          <h1>Request a Quote</h1>
          <h4>
            ${this._subheaderStr}
          </h4>
        </div>
        <div class="form-wrapper">
          ${!this._submitSuccess
            ? html`
                <form
                  id="request-quote-form"
                  autocomplete="on"
                  @submit=${this.onFormSubmit}
                  novalidate
                >
                  ${this._formFields.map(
                    row => html`
                      <div class="input-row">
                        ${row.map(field => this.renderField(field))}
                      </div>
                    `
                  )}
                  <div id="uploads-wrapper">
                    <input
                      type="file"
                      id="request-quote-documents"
                      name="request-quote[images][]"
                      multiple
                      hidden
                    />
                    <label for="request-quote-documents" class="upload-trigger">
                      <img
                        src="/static/icons/SVG/upload-black-12x16.svg"
                        alt=""
                        role="presentation"
                      />
                      <p>Upload supporting documents</p>
                    </label>
                    ${this._fileList.length > 0
                      ? html`
                          <div
                            id="selectedFiles"
                            role="list"
                            aria-label="Selected files"
                          >
                            ${this._fileList.map(
                              (file, index) => html`
                                <span role="listitem">
                                  <p>${file.metadata.filename}</p>
                                  <button
                                    type="button"
                                    class="remove-file"
                                    aria-label=${`Remove ${file.metadata.filename}`}
                                    @click=${() =>
                                      this.removeFile(index, file.id)}
                                  >
                                    remove
                                  </button>
                                </span>
                              `
                            )}
                          </div>
                        `
                      : html``}
                    <p class="fine-print">
                      File uploads are capped at 500MB per file. For files
                      larger than 500MB, please compress them prior to
                      uploading. If you experience any issues, submit this form
                      and then email the files directly to
                      <a href="mailto:rfq@3-form.com">rfq@3-form.com</a>.
                    </p>
                  </div>
                  ${this._showValidationPrompt
                    ? html`
                        <p class="validation-prompt" role="alert">
                          Please correct the highlighted fields above before
                          submitting.
                        </p>
                      `
                    : html``}
                  <cta-button
                    class="cta-btn"
                    cta-data=${this.ctaData('Submit')}
                    cta-type="primary"
                    @click="${this.trySubmit}"
                    ?disabled="${this._submitButtonDisabled}"
                  ></cta-button>
                  <button
                    type="submit"
                    class="visually-hidden-submit"
                    aria-hidden="true"
                    tabindex="-1"
                  >
                    Submit
                  </button>
                </form>
              `
            : html`
                <div class="success-wrapper">
                  <cta-button
                    class="success-cta-btn"
                    cta-data=${this.ctaData('Home')}
                    cta-type="primary"
                    @click="${this.goHome}"
                  ></cta-button>
                  <cta-button
                    class="success-cta-btn"
                    cta-data=${this.ctaData('Quote Another Project')}
                    cta-type="secondary"
                    @click="${this.quoteAnotherProject}"
                  ></cta-button>
                </div>
              `}
          ${this._referenceImageUrl
            ? html`
                <div class="reference-img-wrapper">
                  <img
                    src="${this._referenceImageUrl}"
                    alt="Reference image for quote request"
                  />
                  ${this._referenceImageDescription
                    ? html`
                        <p class="description">
                          ${unsafeHTML(
                            this.withLineBreaks(this._referenceImageDescription)
                          )}
                        </p>
                      `
                    : html``}
                </div>
              `
            : html``}
        </div>
      </div>
    `;
  }

  renderField(field) {
    const showError = field.touched && !field.valid;
    if (field.elementType === 'textarea') {
      return html`
        <tf-textarea
          class="input-wrapper"
          label=${field.label}
          name=${field.id}
          input-id=${field.id}
          ?required=${field.required}
          rows="6"
          .value=${field.value || ''}
          ?invalid=${showError}
          error-text=${showError ? field.errorMessage : ''}
          @input=${e => this.onFieldInput(field, e)}
          @focusout=${() => this.onFieldBlur(field)}
        ></tf-textarea>
      `;
    }
    return html`
      <tf-text-field
        class="input-wrapper"
        label=${field.label}
        name=${field.id}
        input-id=${field.id}
        type=${field.inputType}
        autocomplete=${field.autocomplete || 'off'}
        ?required=${field.required}
        .value=${field.value || ''}
        ?invalid=${showError}
        error-text=${showError ? field.errorMessage : ''}
        @input=${e => this.onFieldInput(field, e)}
        @focusout=${() => this.onFieldBlur(field)}
      ></tf-text-field>
    `;
  }

  populateFields() {
    let previousPageData = JSON.parse(
      window.sessionStorage.getItem('requestInfoData')
    );

    if (!previousPageData) {
      const localStorageRaw = localStorage.getItem('requestInfoData');

      if (localStorageRaw) {
        this.usedLocalStorage = true;
        try {
          previousPageData = JSON.parse(localStorageRaw);
          const nonRequiredFields = [
            'phone',
            'address1',
            'city',
            'state',
            'install_location',
          ];
          this._formFields = this._formFields.map(row =>
            row.map(field => {
              if (nonRequiredFields.includes(field.id)) {
                return {
                  ...field,
                  required: false,
                  valid: true,
                  label: field.label.includes('(optional)')
                    ? field.label
                    : `${field.label} (optional)`,
                };
              }
              return field;
            })
          );
        } catch (e) {
          console.log('Invalid JSON in localStorage requestInfoData:', e);
        }
      }
    }

    if (previousPageData) {
      if (previousPageData.description) {
        const descriptionField = this._flattenedFormFields.find(
          f => f.id === 'description'
        );
        if (descriptionField) {
          descriptionField.value = this.withCarriageReturns(
            previousPageData.description
          );
          descriptionField.touched = true;
          descriptionField.valid = this.updateFieldValidity(descriptionField);
        }
      }

      if (previousPageData.image) {
        this._referenceImageUrl = !!previousPageData.image?.imgObj
          ? previousPageData.image.imgObj
          : previousPageData.image.url;
        this._referenceImageDescription = previousPageData.image.description;
      }

      // A configurator hands over the attributes for a visualization lead,
      // posted alongside the quote once the form is submitted.
      this._visualization = previousPageData.visualization || null;
      if (previousPageData.toolName) {
        this._visualizationTool = previousPageData.toolName;
      }
    } else {
      this._referenceImageUrl = '';
      this._referenceImageDescription = '';
    }
    this.requestUpdate();
  }

  withLineBreaks(string) {
    return !string ? '' : string.split(/\r\n|\n/g).join('<br>');
  }

  withCarriageReturns(string) {
    return !string
      ? ''
      : string
          .replace(/<\/?b>/g, '')
          .split(/<br>/g)
          .join('\n');
  }

  onFieldInput(field, event) {
    field.value = event.target.value;
    field.valid = this.updateFieldValidity(field);

    if (this._formIsValid) {
      this._showValidationPrompt = false;
    }

    // Fields restored from localStorage fire input before the visitor types.
    if (this.completedFirstUpdated) this.formAnalytics.start();

    this.requestUpdate();
  }

  onFieldBlur(field) {
    field.touched = true;
    field.valid = this.updateFieldValidity(field);
    this.requestUpdate();
  }

  updateFieldValidity(fieldObject) {
    if (!fieldObject.required) return true;

    switch (fieldObject.inputType) {
      case 'text':
        return !!fieldObject.value;
      case 'email':
        return /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/.test(
          fieldObject.value || ''
        );
      case 'tel':
        return /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/.test(
          fieldObject.value || ''
        );
      case 'number':
        switch (fieldObject.id) {
          case 'zip':
          case 'install_location':
            return (fieldObject.value || '').length >= 5;
          default:
            return !!fieldObject.value;
        }
      default:
        return !!fieldObject.value;
    }
  }

  onFormSubmit(event) {
    event.preventDefault();
    this.trySubmit();
  }

  trySubmit() {
    if (!this._formIsValid) {
      this.touchEachField();
      this._showValidationPrompt = true;
      this.formAnalytics.error();
      this.requestUpdate();
      this.updateComplete.then(() => this.focusFirstInvalidField());
    } else {
      this.submitFormData(this._formDataObject);
    }
  }

  focusFirstInvalidField() {
    const first = this._flattenedFormFields.find(f => !f.valid);
    if (!first) return;
    const el = this.renderRoot.querySelector(`[input-id="${first.id}"]`);
    if (!el) return;
    const focusInner = () =>
      el.renderRoot?.querySelector('input,textarea')?.focus?.();
    if (el.updateComplete) {
      el.updateComplete.then(focusInner);
    } else {
      focusInner();
    }
  }

  submitFormData(formData) {
    if (this._fileList.length > 0) {
      const fileList = this._fileList.map(file => {
        return {
          file: file,
        };
      });
      formData['quote_request_files_attributes'] = fileList;
    }

    const headers = { 'Content-Type': 'application/json' },
      data = {
        method: 'POST',
        headers,
        body: JSON.stringify({ quote_request: formData }),
      };

    headers[CSRF_HEADER_PARAM] = getCSRFToken();

    this._submitButtonDisabled = true;
    this.requestUpdate();

    fetch('/request-quote', data)
      .then(response => {
        this._submitButtonDisabled = false;
        if (response.ok) {
          this._submitSuccess = true;
          window.scrollTo({ top: 0 });
          this.sendAnalyticsEvent();
          this.formAnalytics.submit();
          if (this._visualization) this.postVisualizationLead(formData);
        } else {
          this.formAnalytics.error();
        }
        this.requestUpdate();
        return response.ok;
      })
      .then(json => {})
      .catch(error => {
        this.formAnalytics.error();
        logErrorToSentry(error);
      });
  }

  // Same lead the Digital Print configurator creates, so a designed pattern
  // reaches the my3form leads dashboard with its configuration attached.
  async postVisualizationLead(formData) {
    const payload = {
      leadAttributes: {
        firstName: formData.first_name,
        lastName: formData.last_name,
        company: formData.company_name,
        email: formData.email,
        phone: formData.phone,
        zipcode: formData.zip,
        country: formData.country,
        projectName: formData.project_name,
        source: 'digital_print_visualization_lead',
      },
      visualizationAttributes: {
        ...this._visualization,
        leadType: 'Digital Print Lead',
        customInstructions: formData.description,
      },
    };

    try {
      const response = await fetch('/add-marketing-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`Visualization lead failed: ${response.status}`);
      }
      fireAdobeAnalyticsEvent({
        type: 'click',
        detail: { eVar8: `INP|RFQ Submitted|${this._visualizationTool}` },
      });
    } catch (error) {
      logErrorToSentry(error);
    }
  }

  removeFile(index, fileId) {
    if (!!fileId && this.uppy) {
      this.uppy.removeFile(fileId);
    }
    this._fileList.splice(index, 1);
    this.requestUpdate();
  }

  touchEachField() {
    this._formFields.forEach(row => {
      row.forEach(field => {
        field.touched = true;
        field.valid = this.updateFieldValidity(field);
      });
    });
  }

  ctaData(text) {
    return JSON.stringify({
      text: text,
      fullWidth: true,
    });
  }

  goHome() {
    window.location = '/';
  }

  sendAnalyticsEvent() {
    pushDataLayerEvent('generate_lead', { lead_type: 'quote_request' });
  }

  quoteAnotherProject() {
    window.location.reload();
  }

  configureUppy() {
    const fileInput = this.shadowRoot.querySelector('#request-quote-documents');
    this.uppy = new Uppy({
      debug: true,
      autoProceed: true,
      restrictions: {
        maxFileSize: 524288000, //500Mb
        maxNumberOfFiles: 10,
        minNumberOfFiles: 1,
      },
    });

    const uppy = this.uppy;
    uppy.use(AwsS3, {
      companionUrl: '/',
    });

    fileInput.addEventListener('change', event => {
      const files = Array.from(event.target.files);
      this._submitButtonDisabled = true;
      this.requestUpdate();

      files.forEach(file => {
        try {
          uppy.addFile({
            source: 'file input',
            name: file.name,
            type: file.type,
            data: file,
          });
        } catch (error) {
          this._submitButtonDisabled = false;
          this.requestUpdate();
          logErrorToSentry(error);
        }
      });
    });

    uppy.on('file-removed', () => {
      this._submitButtonDisabled = false;
      fileInput.value = null;
    });

    uppy.on('complete', () => {
      this._submitButtonDisabled = false;
      fileInput.value = null;
    });

    uppy.on('upload-success', file => {
      this._submitButtonDisabled = false;
      uppy.setState({
        allowNewUpload: true,
      });

      var uploadedFileData = {
        id: file.meta['key'].match(/^cache\/(.+)/)[1],
        storage: 'cache',
        metadata: {
          size: file.size,
          filename: file.name,
          mime_type: file.type,
        },
      };

      let filesAdded = new CustomEvent('files-added', {
        detail: {
          message: 'files-uploaded happened.',
          data: uploadedFileData,
        },
        bubbles: true,
        composed: true,
      });
      this.dispatchEvent(filesAdded);
    });
  }

  addReferenceImageToUppy() {
    const uppy = this.uppy;

    const imageAsBase64 = this._referenceImageUrl.split(',')[1];
    const imageAsBinary = atob(imageAsBase64);
    const bytes = new Uint8Array(
      Array.from(imageAsBinary, char => char.charCodeAt(0))
    );
    const file = new File([bytes], 'web-rendered-image.png', {
      type: 'image/png',
    });

    try {
      this.uppy.addFile({
        source: 'base64',
        name: file.name,
        type: file.type,
        data: file,
      });
    } catch (error) {
      logErrorToSentry(error);
    }
  }

  get _subheaderStr() {
    return this._submitSuccess
      ? 'Thank you for your request. A representative from 3form will contact you within 24 hours.'
      : 'Please provide the information below and we will contact you shortly.';
  }

  get _formIsValid() {
    return this._flattenedFormFields.filter(field => !field.valid).length === 0;
  }

  get _formDataObject() {
    const formDataObject = {};
    this._flattenedFormFields.map(field => {
      formDataObject[field.id] = field.value;
    });
    return formDataObject;
  }

  get _flattenedFormFields() {
    return this._formFields.flat();
  }

  get _formFieldsData() {
    return [
      [
        {
          id: 'first_name',
          label: 'First Name',
          inputType: 'text',
          value: '',
          valid: false,
          required: true,
          errorMessage: 'Please enter your first name',
          autocomplete: 'given-name',
        },
        {
          id: 'last_name',
          label: 'Last Name',
          inputType: 'text',
          value: '',
          valid: false,
          required: true,
          errorMessage: 'Please enter your last name',
          autocomplete: 'family-name',
        },
      ],
      [
        {
          id: 'email',
          label: 'Email',
          inputType: 'email',
          value: '',
          valid: false,
          required: true,
          errorMessage: 'Please enter a valid email address',
          autocomplete: 'email',
        },
        {
          id: 'phone',
          label: 'Phone',
          inputType: 'tel',
          value: '',
          valid: false,
          required: true,
          errorMessage: 'Please enter a valid phone number',
          autocomplete: 'tel-national',
        },
      ],
      [
        {
          id: 'company_name',
          label: 'Company Name',
          inputType: 'text',
          value: '',
          valid: false,
          required: true,
          errorMessage: 'Please enter your company name',
          autocomplete: 'organization',
        },
        {
          id: 'country',
          label: 'Country',
          inputType: 'text',
          value: '',
          valid: false,
          required: true,
          errorMessage: 'Please enter your country',
          autocomplete: 'country',
        },
      ],
      [
        {
          id: 'address1',
          label: 'Address Line 1',
          inputType: 'text',
          value: '',
          valid: false,
          required: true,
          errorMessage: 'Please enter your address',
          autocomplete: 'street-address',
        },
        {
          id: 'address2',
          label: 'Address Line 2 (optional)',
          inputType: 'text',
          value: '',
          valid: true,
          required: false,
          errorMessage: '',
          autocomplete: 'address-line2',
        },
      ],
      [
        {
          id: 'city',
          label: 'City',
          inputType: 'text',
          value: '',
          valid: false,
          required: true,
          errorMessage: 'Please enter your city',
          autocomplete: 'address-level2',
        },
        {
          id: 'state',
          label: 'State/Prov/Region',
          inputType: 'text',
          value: '',
          valid: false,
          required: true,
          errorMessage: 'Please enter your state, province, or region',
          autocomplete: 'address-level1',
        },
        {
          id: 'zip',
          label: 'Zip Code',
          inputType: 'text',
          value: '',
          valid: false,
          required: true,
          errorMessage: 'Please enter your zip or postal code',
          autocomplete: 'postal-code',
        },
      ],
      [
        {
          id: 'project_name',
          label: 'Project Name (As shown on title block)',
          inputType: 'text',
          value: '',
          valid: false,
          required: true,
          errorMessage: 'Please enter the project name',
          autocomplete: 'off',
        },
      ],
      [
        {
          id: 'specifying_designer_company',
          label: 'Specifying Designer Company',
          inputType: 'text',
          value: '',
          valid: false,
          required: true,
          errorMessage: 'Please enter the specifying designer company',
          autocomplete: 'off',
        },
        {
          id: 'install_location',
          label: 'Project Installation Location (Zip Code)',
          inputType: 'text',
          value: '',
          valid: false,
          required: true,
          errorMessage: 'Please enter the install location zip code',
          autocomplete: 'off',
        },
      ],
      [
        {
          id: 'description',
          label: 'Description of Materials and Quantities Needed',
          inputType: 'text',
          elementType: 'textarea',
          value: '',
          valid: false,
          required: true,
          errorMessage: 'Please describe the materials and quantities needed',
          autocomplete: 'off',
        },
      ],
    ];
  }
}

customElements.define('quote-requests', QuoteRequests);
